/**
 * @file formatters.ts
 * @brief Small library of display formatters — dates, money, names.
 *
 * @details
 * Centralized so all surfaces format values identically and locale
 * changes only need a single update.
 *
 * @author DES
 * @date 26-05-2026
 */

import { format, formatDistanceToNowStrict, isValid, parseISO } from "date-fns";

// ----------------------------------------------------------------------
// Dates
// ----------------------------------------------------------------------
export function toDate(input: string | Date | null | undefined): Date | null {
  if (!input) return null;
  if (input instanceof Date) return isValid(input) ? input : null;
  const parsed = parseISO(input);
  return isValid(parsed) ? parsed : null;
}

export function formatDate(
  input: string | Date | null | undefined,
  pattern = "dd MMM yyyy",
): string {
  const d = toDate(input);
  return d ? format(d, pattern) : "—";
}

export function formatDateTime(
  input: string | Date | null | undefined,
): string {
  const d = toDate(input);
  return d ? format(d, "dd MMM yyyy, HH:mm") : "—";
}

export function formatRelative(
  input: string | Date | null | undefined,
): string {
  const d = toDate(input);
  if (!d) return "—";
  return `${formatDistanceToNowStrict(d, { addSuffix: true })}`;
}

// ----------------------------------------------------------------------
// Money
// Minor units (integer). Default display currency GBP matches the UK-first
// product convention (same numeral across USD/EUR/GBP — pricing-model.md).
// Prefer passing the row's `currency` when available; aggregates of mixed
// currencies remain same-numeral sums and use this default for the symbol.
// ----------------------------------------------------------------------
export function formatMoneyMinor(
  minorUnits: number | string | null | undefined,
  currency = "GBP",
  locale = "en-GB",
): string {
  if (minorUnits == null) return "—";
  const n = typeof minorUnits === "string" ? Number(minorUnits) : minorUnits;
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    // "$27.99" for US customers, not en-GB's "US$27.99" (USD pricing, 18-10-2026).
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n / 100);
}

// ----------------------------------------------------------------------
// Duration — accepts seconds (or a string from Postgres) and returns
// `H:MM:SS` for >=1h, `M:SS` for >=1m, `Ns` otherwise. Used by the
// Sessions list to show clip length without crowding the column.
// DES Added: (26-05-2026) — related: features/sessions/SessionsPage.tsx
// ----------------------------------------------------------------------
export function formatDurationSeconds(
  input: number | string | null | undefined,
): string {
  if (input == null) return "—";
  const total =
    typeof input === "string" ? Math.floor(Number(input)) : Math.floor(input);
  if (!Number.isFinite(total) || total < 0) return "—";
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  if (minutes > 0) return `${minutes}:${pad(seconds)}`;
  return `${seconds}s`;
}

// ----------------------------------------------------------------------
// Language pair — returns "EN → UR" style label for the session table.
// Falls back gracefully when one or both codes are missing.
// ----------------------------------------------------------------------
export function formatLanguagePair(
  source?: string | null,
  target?: string | null,
): string {
  const s = source?.toUpperCase() || "—";
  const t = target?.toUpperCase() || "—";
  if (s === "—" && t === "—") return "—";
  return `${s} → ${t}`;
}

// ----------------------------------------------------------------------
// Currency symbol — derives the symbol from an ISO code without going
// through `Intl.NumberFormat` (which always wraps the number too).
// Useful when the symbol and number are styled separately, e.g. the
// hero "£12.34" cost row in the session detail dialog where the
// symbol is brand-coloured and the number is foreground.
// Mirrors the helper in `FRONTEND/src/shared/context/BillingContext.tsx`.
// DES Added: (26-05-2026)
// ----------------------------------------------------------------------
const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: "£",
  USD: "$",
  EUR: "€",
  AUD: "A$",
  CAD: "C$",
  INR: "₹",
  JPY: "¥",
  CNY: "¥",
  AED: "AED",
  SAR: "SAR",
  PKR: "₨",
};

export function symbolFromCurrency(code?: string | null): string {
  if (!code) return "£";
  const upper = code.toUpperCase();
  return CURRENCY_SYMBOLS[upper] ?? upper;
}

/**
 * Convert a minor-units amount into a `[symbol, amount]` tuple so the
 * caller can render the two halves with different styles. When `value`
 * is missing we return `[symbol, "—"]` so the caller can keep its
 * layout stable.
 */
export function splitMoneyMinor(
  minorUnits: number | string | null | undefined,
  currency = "GBP",
): [symbol: string, amount: string] {
  const symbol = symbolFromCurrency(currency);
  if (minorUnits == null) return [symbol, "—"];
  const n = typeof minorUnits === "string" ? Number(minorUnits) : minorUnits;
  if (!Number.isFinite(n)) return [symbol, "—"];
  return [symbol, (n / 100).toFixed(2)];
}

// ----------------------------------------------------------------------
// RTL detection — Unicode-range check for Hebrew, Arabic, Urdu, Persian
// (matches `FRONTEND/src/shared/components/VZUIKit/VZTranscriptBubble.tsx::isRTL`).
// Used by the session-detail utterances list so original / translated
// text is visually correct for languages like Urdu and Arabic.
// DES Added: (26-05-2026)
// ----------------------------------------------------------------------
const RTL_RANGES =
  /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

export function isRTLText(text?: string | null): boolean {
  if (!text) return false;
  return RTL_RANGES.test(text);
}

// ----------------------------------------------------------------------
// People
// ----------------------------------------------------------------------
export function formatFullName(
  firstName?: string | null,
  lastName?: string | null,
): string {
  const parts = [firstName, lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "—";
}

export function getInitials(
  firstName?: string | null,
  lastName?: string | null,
  fallbackEmail?: string | null,
): string {
  const f = firstName?.[0];
  const l = lastName?.[0];
  if (f && l) return `${f}${l}`.toUpperCase();
  if (f) return f.toUpperCase();
  if (fallbackEmail?.[0]) return fallbackEmail[0].toUpperCase();
  return "?";
}
